import {
  ColumnDef,
  ColumnSizingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  RowSelectionState,
  SortingFn,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { GridRenderCellParams, GridValidRowModel } from '@mui/x-data-grid'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { com } from '@eidu/form'
import { NavigateFunction } from 'react-router-dom'
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { ArrowDownward, ArrowUpward, FilterList, MoreVert } from '@mui/icons-material'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import EqualityHashMap from '../../util/EqualityHashMap'
import EntityWithLabel from '../../domain/entity/EntityWithLabel'
import { fieldValueToString } from '../../domain/entity/DraftFieldValue'
import EntityRepository from '../../io/EntityRepository'
import usePaginatedEntitiesOfType from '../../io/usePaginatedEntitiesOfType'
import useHasGlobalPermissions from '../../pages/permission/useHasGlobalPermissions'
import useEntityCount from '../../io/useEntityCount'
import getRenderFieldCell, { CellContent } from '../misc/getRenderFieldCell'
import getRenderInvitationCell from '../misc/getRenderInvitationCell'
import BulkEditMenu from './BulkEditMenu'
import FilterPopover, { NULL_FILTER_VALUE } from './FilterPopover'
import useDebounced from '../../ui/debounce/useDebounced'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityId = com.eidu.sharedlib.entity.EntityId
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import Field = com.eidu.sharedlib.entity.field.Field
import FieldId = com.eidu.sharedlib.entity.field.FieldId
import FieldType = com.eidu.sharedlib.entity.field.FieldType
import entityIdFromStringOrNull = com.eidu.sharedlib.entity.entityIdFromStringOrNull
import entityIdFromString = com.eidu.sharedlib.entity.entityIdFromString
import phoneNumberLoginIdentifierKey = com.eidu.sharedlib.entity.phoneNumberLoginIdentifierKey
import emailLoginIdentifierKey = com.eidu.sharedlib.entity.emailLoginIdentifierKey
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind
import invitationStatusKey = com.eidu.sharedlib.entity.invitationStatusKey

const SortIndicator = ({ direction }: { direction: false | 'asc' | 'desc' }) => {
  if (direction === 'asc') return <ArrowUpward sx={{ fontSize: 16 }} />
  if (direction === 'desc') return <ArrowDownward sx={{ fontSize: 16 }} />
  return null
}

const getIdMap = (entities: readonly EntityWithLabelAndRelated[]): ReadonlyMap<EntityId, EntityWithLabel> =>
  new EqualityHashMap(
    entities.flatMap((entity) => [
      [entity.entity.id, entity] as [EntityId, EntityWithLabel],
      ...entity.referencedEntities.map((ref) => [ref.entity.id, ref] as [EntityId, EntityWithLabel]),
    ])
  )

const naturalCompare = (a: string | undefined | null, b: string | undefined | null): number => {
  if (a == null && b == null) return 0
  if (a == null) return -1
  if (b == null) return 1
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
}

const naturalSortingFn: SortingFn<TableRowData> = (rowA, rowB, columnId) => {
  const a = rowA.original.sortValues[columnId] ?? (rowA.getValue(columnId) as string | undefined)
  const b = rowB.original.sortValues[columnId] ?? (rowB.getValue(columnId) as string | undefined)
  return naturalCompare(a, b)
}

type TableRowData = {
  entityId: string
  rowNumber: number
  entity: EntityWithLabelAndRelated
  fieldValues: Record<string, string | undefined>
  sortValues: Record<string, string | undefined>
  phoneNumberLoginIdentifier?: string
  emailLoginIdentifier?: string
  invitationStatus?: string
}

type TanStackDataGridProps = {
  navigate: NavigateFunction
  entityRepository: EntityRepository
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
  entityType: EntityType
  pageSize: number
}

const TanStackDataGrid = ({ navigate, entityRepository, entityTypes, entityType, pageSize }: TanStackDataGridProps) => {
  const { hasPermission } = useHasGlobalPermissions(GlobalAction.ManageAuthentication)

  const showAuthenticationInfo =
    entityType.kind.equals(EntityTypeKind.User) && hasPermission(GlobalAction.ManageAuthentication)

  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery] = useDebounced(searchQuery)

  const [columnFilters, setColumnFilters] = useState<Map<FieldId, string>>(new Map())
  const [filterLabels, setFilterLabels] = useState<Map<FieldId, string>>(new Map())
  const [debouncedFilters] = useDebounced(columnFilters)

  const totalCount = useEntityCount({
    typeId: entityType.id,
    searchQuery: debouncedSearchQuery,
    filters: debouncedFilters,
  })

  const { entities, loadMore, reload, hasMore, loading } = usePaginatedEntitiesOfType({
    typeId: entityType.id,
    searchQuery: debouncedSearchQuery,
    filters: debouncedFilters,
    allTypes: entityTypes,
    pageSize,
  })

  const entityIdMap = useMemo(() => getIdMap(entities), [entities])

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({})
  const [sorting, setSorting] = useState<SortingState>([])
  const [bulkEditMenu, setBulkEditMenu] = useState<{ anchorEl: HTMLElement; field: Field } | null>(null)
  const [filterPopover, setFilterPopover] = useState<{ anchorEl: HTMLElement; field: Field } | null>(null)

  useEffect(() => {
    setRowSelection({})
  }, [entityType.id, searchQuery, columnFilters])

  useEffect(() => {
    setColumnFilters(new Map())
    setFilterLabels(new Map())
  }, [entityType.id])

  const handleFilterChange = (fieldId: FieldId, value: string | null, label?: string) => {
    setColumnFilters((prev) => {
      const next = new EqualityHashMap(prev)
      if (value === null) {
        next.delete(fieldId)
      } else {
        next.set(fieldId, value)
      }
      return next
    })
    setFilterLabels((prev) => {
      const next = new EqualityHashMap(prev)
      if (value === null || !label) {
        next.delete(fieldId)
      } else {
        next.set(fieldId, label)
      }
      return next
    })
  }

  const handleClearFilter = (fieldId: FieldId) => {
    handleFilterChange(fieldId, null)
  }

  const handleClearAllFilters = () => {
    setColumnFilters(new Map())
    setFilterLabels(new Map())
  }

  const formatFilterValue = (value: string, field: Field): string => {
    if (value === NULL_FILTER_VALUE) return 'empty'
    const label = filterLabels.get(field.id)
    if (label) return label
    return value
  }

  const tableData: TableRowData[] = useMemo(
    () =>
      entities.map((entity, index) => {
        const fieldValues = Object.fromEntries(
          entityType.fields.asJsReadonlyArrayView().map((field) => [
            `field-${field.id.toString()}`,
            entity.entity.valuesByFieldId
              .asJsReadonlyMapView()
              .get(field.id)
              ?.let((it) => fieldValueToString(it)),
          ])
        )

        const sortValues = Object.fromEntries(
          entityType.fields.asJsReadonlyArrayView().map((field) => {
            const fieldKey = `field-${field.id.toString()}`
            const rawValue = fieldValues[fieldKey]

            if (field.type === FieldType.Reference && rawValue) {
              const refId = entityIdFromStringOrNull(rawValue)
              if (refId) {
                const refEntity = entityIdMap.get(refId)
                return [fieldKey, refEntity?.primaryLabelText ?? rawValue]
              }
            }
            return [fieldKey, rawValue]
          })
        )

        return {
          entityId: entity.entity.id.toString(),
          rowNumber: index + 1,
          entity,
          fieldValues,
          sortValues,
          ...(showAuthenticationInfo
            ? {
                phoneNumberLoginIdentifier:
                  entity.entity.externalValues.asJsReadonlyMapView().get(phoneNumberLoginIdentifierKey) || '',
                emailLoginIdentifier:
                  entity.entity.externalValues.asJsReadonlyMapView().get(emailLoginIdentifierKey) || '',
                invitationStatus: entity.entity.externalValues.asJsReadonlyMapView().get(invitationStatusKey) || '',
              }
            : {}),
        }
      }),
    [entities, entityType.fields, showAuthenticationInfo, entityIdMap]
  )

  const entityIdMapRef = useRef(entityIdMap)
  entityIdMapRef.current = entityIdMap

  const reloadRef = useRef(reload)
  reloadRef.current = reload

  const columns = useMemo<ColumnDef<TableRowData>[]>(() => {
    const fieldColumns: ColumnDef<TableRowData>[] = entityType.fields.asJsReadonlyArrayView().map((field) => ({
      id: `field-${field.id.toString()}`,
      header: ({ column }) => {
        // Text fields only support exact match filtering, which would be confusing alongside the fuzzy search
        const isFilterable = field.type !== FieldType.Text
        return (
          <>
            <Box
              key="label"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 0,
                cursor: 'pointer',
                flex: 1,
                pr: isFilterable ? 7 : 4,
              }}
              onClick={column.getToggleSortingHandler()}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{field.name}</span>
              <SortIndicator direction={column.getIsSorted()} />
            </Box>
            {isFilterable && (
              <Tooltip key="filter" title="Filter">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    setFilterPopover({ anchorEl: e.currentTarget as HTMLElement, field })
                  }}
                  sx={{
                    padding: 0.5,
                    position: 'absolute',
                    right: 32,
                    color: columnFilters.has(field.id) ? 'primary.main' : 'inherit',
                  }}
                  aria-label={`Filter by ${field.name}`}
                >
                  <FilterList fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              key="menu"
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                setBulkEditMenu({ anchorEl: e.currentTarget as HTMLElement, field })
              }}
              sx={{ padding: 0.5, position: 'absolute', right: 8 }}
              aria-label={`Menu for ${field.name}`}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </>
        )
      },
      accessorFn: (row) => row.fieldValues[`field-${field.id.toString()}`],
      cell: (info) => {
        const cellContent: CellContent = { value: info.getValue() as string | undefined }
        const renderCell = getRenderFieldCell(field.type, entityIdMapRef.current, entityTypes)
        return renderCell({
          value: cellContent,
          row: { id: info.row.original.entityId },
        } as unknown as GridRenderCellParams<GridValidRowModel, CellContent>)
      },
      sortingFn: naturalSortingFn,
      size: 200,
      minSize: 100,
    }))

    const baseColumns: ColumnDef<TableRowData>[] = [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
            inputProps={{ 'aria-label': 'Select all rows' }}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onChange={row.getToggleSelectedHandler()}
            onClick={(e) => e.stopPropagation()}
            inputProps={{ 'aria-label': `Select row ${row.original.rowNumber}` }}
          />
        ),
        size: 50,
        enableResizing: false,
        enableSorting: false,
      },
      {
        id: 'row',
        header: ({ column }) => (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
            onClick={column.getToggleSortingHandler()}
          >
            <span>Row</span>
            <SortIndicator direction={column.getIsSorted()} />
          </Box>
        ),
        accessorKey: 'rowNumber',
        sortingFn: 'basic',
        size: 70,
        minSize: 50,
      },
    ]

    const authColumns: ColumnDef<TableRowData>[] = showAuthenticationInfo
      ? [
          {
            id: 'phoneNumberLoginIdentifier',
            header: ({ column }) => (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                onClick={column.getToggleSortingHandler()}
              >
                <span>Access Phone Number</span>
                <SortIndicator direction={column.getIsSorted()} />
              </Box>
            ),
            accessorKey: 'phoneNumberLoginIdentifier',
            sortingFn: naturalSortingFn,
            size: 180,
            minSize: 100,
          },
          {
            id: 'emailLoginIdentifier',
            header: ({ column }) => (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                onClick={column.getToggleSortingHandler()}
              >
                <span>Access Email</span>
                <SortIndicator direction={column.getIsSorted()} />
              </Box>
            ),
            accessorKey: 'emailLoginIdentifier',
            sortingFn: naturalSortingFn,
            size: 180,
            minSize: 100,
          },
          {
            id: 'invitationStatus',
            header: ({ column }) => (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                onClick={column.getToggleSortingHandler()}
              >
                <span>Invitation</span>
                <SortIndicator direction={column.getIsSorted()} />
              </Box>
            ),
            accessorKey: 'invitationStatus',
            sortingFn: naturalSortingFn,
            size: 100,
            minSize: 80,
            cell: (info) => {
              const renderCell = getRenderInvitationCell(() => reloadRef.current())
              return renderCell({
                value: info.getValue() as string,
                row: {
                  id: info.row.original.entityId,
                  phoneNumberLoginIdentifier: info.row.original.phoneNumberLoginIdentifier,
                  emailLoginIdentifier: info.row.original.emailLoginIdentifier,
                },
              } as unknown as GridRenderCellParams<GridValidRowModel, string>)
            },
          },
        ]
      : []

    return [...baseColumns, ...fieldColumns, ...authColumns]
  }, [entityType.fields, entityTypes, showAuthenticationInfo])

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      rowSelection,
      columnSizing,
      sorting,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onColumnSizingChange: setColumnSizing,
    onSortingChange: setSorting,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.entityId,
  })

  const tableContainerRef = useRef<HTMLDivElement>(null)

  const { rows } = table.getRowModel()

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 45,
    overscan: 5,
  })

  const virtualRows = rowVirtualizer.getVirtualItems()
  const lastVirtualRowIndex = virtualRows[virtualRows.length - 1]?.index ?? -1

  useEffect(() => {
    if (lastVirtualRowIndex >= rows.length - 1 && hasMore && !loading) {
      loadMore()
    }
  }, [hasMore, loading, rows.length, lastVirtualRowIndex, loadMore])

  const selectedEntityIds = Object.keys(rowSelection)

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          backgroundColor: 'background.paper',
          py: 1,
          px: 1,
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <TextField
          label="Search"
          inputProps={{ 'data-testid': 'entity-search-input' }}
          size="small"
          value={searchQuery}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setSearchQuery(event.target.value)
          }}
          sx={{ minWidth: 250 }}
        />
        <Typography data-testid="entity-selected-rows-text" variant="body2" color="primary">
          {selectedEntityIds.length > 0
            ? `${selectedEntityIds.length} row${selectedEntityIds.length === 1 ? '' : 's'} selected`
            : ''}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {totalCount !== undefined ? `${totalCount} total` : 'Loading count...'}
        </Typography>
      </Box>
      {columnFilters.size > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            px: 1,
            py: 0.5,
            backgroundColor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          {Array.from(columnFilters.entries()).map(([fieldId, value]) => {
            const field = entityType.fields.asJsReadonlyArrayView().find((f) => f.id.equals(fieldId))
            return field ? (
              <Chip
                key={fieldId.toString()}
                label={`${field.name}: ${formatFilterValue(value, field)}`}
                onDelete={() => handleClearFilter(fieldId)}
                size="small"
                color="primary"
                variant="outlined"
              />
            ) : null
          })}
          <Chip label="Clear all" onClick={handleClearAllFilters} size="small" variant="outlined" />
        </Box>
      )}
      <Box
        ref={tableContainerRef}
        data-testid="entity-table-container"
        sx={{
          flex: 1,
          overflow: 'auto',
        }}
      >
        <Table
          component="div"
          role="table"
          size="small"
          sx={{ tableLayout: 'fixed', minWidth: 'max-content', display: 'block' }}
        >
          <TableHead
            component="div"
            role="rowgroup"
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              display: 'block',
            }}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                component="div"
                role="row"
                sx={{ display: 'flex', backgroundColor: 'background.paper', minWidth: '100%' }}
              >
                {headerGroup.headers.map((header, index) => (
                  <TableCell
                    key={header.id}
                    component="div"
                    role="columnheader"
                    sx={{
                      width: header.getSize(),
                      minWidth: header.column.columnDef.minSize ?? header.getSize(),
                      flexGrow: index === headerGroup.headers.length - 1 ? 1 : 0,
                      position: 'relative',
                      backgroundColor: 'inherit',
                      fontWeight: 'bold',
                      py: 1,
                      display: 'flex',
                      alignItems: 'center',
                      overflow: 'hidden',
                      borderBottom: 1,
                      borderColor: 'divider',
                    }}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanResize() && (
                      <Box
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        sx={{
                          position: 'absolute',
                          right: 1,
                          top: '25%',
                          height: '50%',
                          width: 2,
                          cursor: 'col-resize',
                          userSelect: 'none',
                          touchAction: 'none',
                          borderRadius: 0.5,
                          backgroundColor: header.column.getIsResizing() ? 'primary.main' : 'divider',
                          '&:hover': {
                            backgroundColor: 'primary.main',
                          },
                        }}
                      />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody
            component="div"
            role="rowgroup"
            sx={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              position: 'relative',
              display: 'block',
            }}
          >
            {rows.length === 0 && !loading ? (
              <TableRow component="div" role="row" sx={{ display: 'flex', width: '100%' }}>
                <TableCell component="div" role="cell" sx={{ flex: 1, textAlign: 'center', border: 'none' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No entities found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {virtualRows.map((virtualRow) => {
                  const row = rows[virtualRow.index]
                  return (
                    <TableRow
                      key={row.id}
                      component="div"
                      role="row"
                      data-index={virtualRow.index}
                      ref={rowVirtualizer.measureElement}
                      onClick={() => {
                        const id = entityIdFromString(row.id)
                        if (id) navigate(`/entities/${entityType.id.toString()}/view/${id.toString()}`)
                      }}
                      sx={{
                        cursor: 'pointer',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualRow.start}px)`,
                        display: 'flex',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      {row.getVisibleCells().map((cell, index) => (
                        <TableCell
                          key={cell.id}
                          component="div"
                          role="cell"
                          sx={{
                            width: cell.column.getSize(),
                            flexGrow: index === row.getVisibleCells().length - 1 ? 1 : 0,
                            minWidth: 0,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <Box
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  )
                })}
              </>
            )}
          </TableBody>
        </Table>
        {loading && (
          <Box
            sx={{
              py: 2,
              position: 'sticky',
              left: 0,
              width: tableContainerRef.current?.clientWidth ?? '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          </Box>
        )}
      </Box>
      {bulkEditMenu && (
        <BulkEditMenu
          field={bulkEditMenu.field}
          anchorEl={bulkEditMenu.anchorEl}
          onClose={() => setBulkEditMenu(null)}
          selectedEntityIds={selectedEntityIds}
          entityRepository={entityRepository}
          entityTypes={entityTypes}
          onEditComplete={() => {
            reload()
            setRowSelection({})
          }}
        />
      )}
      {filterPopover && (
        <FilterPopover
          field={filterPopover.field}
          currentValue={columnFilters.get(filterPopover.field.id) ?? null}
          onApply={(value, label) => handleFilterChange(filterPopover.field.id, value, label)}
          onClear={() => handleClearFilter(filterPopover.field.id)}
          anchorEl={filterPopover.anchorEl}
          onClose={() => setFilterPopover(null)}
          entityTypes={entityTypes}
        />
      )}
    </Box>
  )
}

export default TanStackDataGrid
